import { useEffect, useRef, useState, useCallback } from 'react';

export type WebSocketState = 'connecting' | 'connected' | 'disconnected' | 'reconnecting';

interface UseWebSocketOptions {
  url: string;
  token: string | null;
  onMessage?: (data: unknown) => void;
  onError?: (error: Event) => void;
}

/**
 * Calculate reconnect delay with progressive backoff and jitter.
 * Never gives up - uses increasingly conservative delays for extended outages.
 */
const calculateReconnectDelay = (attempt: number): number => {
  let delay: number;
  if (attempt <= 5) {
    // Aggressive: 1s, 2s, 4s, 8s, 16s
    delay = Math.min(1000 * Math.pow(2, attempt - 1), 16000);
  } else if (attempt <= 15) {
    // Moderate: 30s
    delay = 30000;
  } else {
    // Conservative: 2-5 minutes
    delay = 120000 + Math.random() * 180000;
  }
  // Add 10-20% jitter
  return delay + delay * (0.1 + Math.random() * 0.1);
};

interface UseWebSocketReturn {
  state: WebSocketState;
  send: (data: unknown) => void;
  reconnect: () => void;
}

export function useWebSocket({
  url,
  token,
  onMessage,
  onError,
}: UseWebSocketOptions): UseWebSocketReturn {
  const [state, setState] = useState<WebSocketState>('disconnected');
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<number | null>(null);

  // Use refs for callbacks to avoid reconnection loops when callbacks change
  const onMessageRef = useRef(onMessage);
  const onErrorRef = useRef(onError);

  // Update refs when callbacks change (without triggering reconnect)
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  const connect = useCallback(() => {
    if (!token) {
      setState('disconnected');
      return;
    }

    // Clean up existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }

    setState('connecting');

    try {
      // Build WebSocket URL with token
      const wsUrl = url.startsWith('http')
        ? url.replace(/^http/, 'ws')
        : url.startsWith('ws')
          ? url
          : `ws://${url}`;

      const fullUrl = `${wsUrl}?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(fullUrl);

      ws.onopen = () => {
        setState('connected');
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          onMessageRef.current?.(data);
        } catch {
          // Non-JSON message
          onMessageRef.current?.(event.data);
        }
      };

      ws.onerror = (error) => {
        onErrorRef.current?.(error);
      };

      ws.onclose = () => {
        setState('disconnected');
        wsRef.current = null;

        // Always attempt reconnection - never give up
        reconnectAttemptsRef.current += 1;
        setState('reconnecting');

        const delay = calculateReconnectDelay(reconnectAttemptsRef.current);
        reconnectTimeoutRef.current = window.setTimeout(() => {
          connect();
        }, delay);
      };

      wsRef.current = ws;
    } catch {
      setState('disconnected');
    }
  }, [url, token]);

  const send = useCallback((data: unknown) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    }
  }, []);

  const reconnect = useCallback(() => {
    reconnectAttemptsRef.current = 0;
    connect();
  }, [connect]);

  useEffect(() => {
    connect();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connect]);

  return { state, send, reconnect };
}
